import React, { useRef, useState } from "react";
import { api, apiErrorMessage, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";

export default function PhotoUploader({ photos, onChange, max = 10 }) {
  const { t } = useLocale();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = max - photos.length;
    const list = Array.from(files).slice(0, remaining);
    setUploading(true);
    const uploaded = [...photos];
    for (const f of list) {
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/photos/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.path);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      }
    }
    onChange(uploaded);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">{t("form.f_photos")}</div>
          <p className="text-xs text-muted-foreground mt-1">{t("form.f_photos_hint")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || photos.length >= max}
          data-testid="photo-upload-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {t("form.upload")}
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid="photo-file-input"
      />
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {photos.map((p, idx) => (
            <div key={p} className="relative aspect-square overflow-hidden rounded-sm border border-border group">
              <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => remove(idx)}
                className="absolute top-1 right-1 rounded-full bg-background/85 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`photo-remove-${idx}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
